import { useState } from 'react'
import type { ClubEvent } from '../../types'
import { EVENT_TYPES, eventMeta, parseLocalDate } from './eventMeta'
import { createClubEvent, updateClubEvent, deleteClubEvent, type ClubEventInput } from '../../api/clubs'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const EMPTY: ClubEventInput = {
  type: 'race',
  title: '',
  date: '',
  time: '',
  endDate: '',
  location: '',
  description: '',
}

const inputClass =
  'w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'

function sortEvents(list: ClubEvent[]): ClubEvent[] {
  return [...list].sort(
    (a, b) => a.date.localeCompare(b.date) || (a.time || '').localeCompare(b.time || ''),
  )
}

/** Secretary tool to manage a club's combined-calendar events. */
export function EventManager({ clubNum, initialEvents }: { clubNum: number; initialEvents: ClubEvent[] }) {
  const [events, setEvents] = useState<ClubEvent[]>(() => sortEvents(initialEvents))
  const [editingId, setEditingId] = useState<number | 'new' | null>(null)
  const [form, setForm] = useState<ClubEventInput>(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

  function startAdd() {
    setForm(EMPTY)
    setEditingId('new')
    setError('')
  }

  function startEdit(e: ClubEvent) {
    setForm({
      type: e.type,
      title: e.title,
      date: e.date,
      time: e.time,
      endDate: e.endDate,
      location: e.location,
      description: e.description,
    })
    setEditingId(e.id)
    setError('')
  }

  async function save() {
    if (!form.title.trim() || !form.date) {
      setError('Title and date are required.')
      return
    }
    setSaving(true)
    setError('')
    const normalized: ClubEvent = {
      id: typeof editingId === 'number' ? editingId : 0,
      type: form.type,
      title: form.title.trim(),
      date: form.date,
      time: form.time || '',
      endDate: form.endDate || '',
      location: form.location || '',
      description: form.description || '',
    }
    try {
      if (editingId === 'new') {
        const { id } = await createClubEvent(clubNum, form)
        setEvents((prev) => sortEvents([...prev, { ...normalized, id }]))
      } else if (typeof editingId === 'number') {
        await updateClubEvent(clubNum, editingId, form)
        setEvents((prev) => sortEvents(prev.map((e) => (e.id === editingId ? normalized : e))))
      }
      setEditingId(null)
    } catch {
      setError('Failed to save the event. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  async function remove(id: number) {
    setConfirmDeleteId(null)
    setError('')
    const prev = events
    setEvents(events.filter((e) => e.id !== id))
    try {
      await deleteClubEvent(clubNum, id)
    } catch {
      setEvents(prev)
      setError('Failed to delete the event.')
    }
  }

  return (
    <div className="border border-border rounded-lg p-6 bg-surface">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-heading font-semibold text-text">Events &amp; Announcements</h2>
        {editingId === null && (
          <button
            type="button"
            onClick={startAdd}
            className="px-4 py-2 bg-primary text-text-inverse rounded-md hover:bg-primary-light transition-colors text-sm"
          >
            + Add Event
          </button>
        )}
      </div>
      <p className="text-sm text-text-muted mb-4">
        One combined calendar: races, meetings, liberation times, socials, and announcements all
        show up on your public page as upcoming events.
      </p>

      {error && (
        <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {editingId !== null && (
        <div className="border border-border rounded-md p-4 mb-4 bg-surface-alt">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text mb-1">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value as ClubEventInput['type'] })}
                className={inputClass}
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Liberation - Race from Springfield"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Time (optional)</label>
              <input
                type="text"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                placeholder="e.g. 8:00 AM"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">End date (optional)</label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Location (optional)</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g. Club loft"
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-text mb-1">Details (optional)</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className={inputClass}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-5 py-2 bg-primary text-text-inverse rounded-md hover:bg-primary-light transition-colors text-sm disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingId === 'new' ? 'Add Event' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingId(null)
                setError('')
              }}
              className="px-4 py-2 border border-border rounded-md text-text hover:bg-surface-hover transition-colors text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {events.length === 0 ? (
        <p className="text-sm text-text-muted">No events yet. Add your first one above.</p>
      ) : (
        <ul className="divide-y divide-border">
          {events.map((e) => {
            const meta = eventMeta(e.type)
            const d = parseLocalDate(e.date)
            return (
              <li key={e.id} className="py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${meta.chip}`}>
                      {meta.label}
                    </span>
                    <span className="font-medium text-text">{e.title}</span>
                  </div>
                  <p className="text-sm text-text-muted mt-0.5">
                    {MONTHS[d.getMonth()]} {d.getDate()}, {d.getFullYear()}
                    {e.time ? ` · ${e.time}` : ''}
                    {e.location ? ` · ${e.location}` : ''}
                  </p>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  {confirmDeleteId === e.id ? (
                    <>
                      <span className="text-xs text-text-muted">Delete?</span>
                      <button
                        type="button"
                        onClick={() => remove(e.id)}
                        className="text-sm text-error hover:underline"
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        className="text-sm text-text-muted hover:underline"
                      >
                        No
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => startEdit(e)}
                        className="text-sm text-primary hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(e.id)}
                        className="text-sm text-text-muted hover:text-error"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
